/**
 * Meta (Facebook/Instagram) Pixel — measures which ads bring people who sign
 * up (2026-10-08). Dataset "TrybeUP website" in the VERIFIED "Trybeup"
 * business portfolio (3095402617374738) since 2026-10-09; the first dataset
 * (1088595780737580, in a second, unverified portfolio) is retired.
 *
 * Deliberately narrow, because TrybeUP holds wellbeing data and Meta should
 * see none of it:
 *  - WEBSITE ONLY, on trybeup.com. Never in the iOS/Android apps (Apple treats
 *    an ad pixel there as tracking that needs the App Tracking Transparency
 *    prompt) and never on dev/preview hosts.
 *  - Loads lazily on the first funnel event below — the public pages, plus a
 *    NEW account's first signed-in load (CompleteRegistration) — so someone
 *    already using the app never downloads it.
 *  - Only the named funnel steps in META_EVENTS are sent. `autoConfig` is off
 *    (no automatic button-click / page-metadata events) and so is history
 *    tracking (no page view per in-app navigation).
 *  - Never sends while the address holds sign-in keys or the Pilot hand-off
 *    problem (lib/safeUrl.ts): Meta records the page address with each event.
 *  - Follows the same consent choice as PostHog (lib/analyticsConsent.ts): UK/EU
 *    visitors only after Accept; Decline anywhere switches it off.
 *  - CompleteRegistration also goes to our server, which sends Meta the same
 *    event (Conversions API, backend/services/meta_capi.py, 2026-10-27) — only
 *    from here, so the same gates apply to both copies.
 */
import { Capacitor } from "@capacitor/core";
import { currentAnalyticsMode, isEmbedded, type AnalyticsMode } from "@/lib/analyticsConsent";
import { hasUrlSecrets } from "@/lib/safeUrl";
import { getStoredUtm, validFbclid } from "@/lib/utmCapture";

export const META_PIXEL_ID = "1418342223734629";
const PIXEL_HOSTS = new Set(["trybeup.com", "www.trybeup.com"]);
/** The account id this device last sent CompleteRegistration for. */
const REGISTERED_KEY = "trybeup_meta_registered";
/** An account counts as a new sign-up for this long after it's created (the
 *  same 48h window as the signup attribution in lib/utmCapture.ts). */
const NEW_ACCOUNT_MS = 48 * 60 * 60 * 1000;

/** Our PostHog funnel events → Meta standard events. Nothing else is sent. */
export function metaEventFor(event: string, props: Record<string, unknown> = {}): string | null {
  switch (event) {
    case "gateway.viewed":
    case "landing.viewed":
      return "PageView";
    case "gateway.preview.shown":
      return "ViewContent"; // typed a problem and saw Pilot's first step
    case "gateway.cta.clicked":
    case "landing.cta.clicked":
      return props.cta === "signup" ? "Lead" : null; // sign-up started
    case "signup_completed":
      // The email sign-up form was sent (before confirming). Once per page
      // load, so after a sign-up button tap this is the same Lead.
      return "Lead";
    // CompleteRegistration is NOT mapped from an event: it comes from
    // trackMetaRegistration (a new account's first signed-in load). It used
    // to be profile_gate_completed — but email sign-ups send name, birth year
    // and sex with the form, never see that card, and so never counted, while
    // older accounts filling a missing field counted as new (review 2026-10-09).
    default:
      return null;
  }
}

/** Pure: may the pixel run here at all, and right now? */
export function pixelAllowed(opts: { host: string; native: boolean; mode: AnalyticsMode; embedded?: boolean }): boolean {
  return !opts.native && !opts.embedded && PIXEL_HOSTS.has(opts.host) && opts.mode === "full";
}

/** Pure: was this account created recently enough to count as a sign-up? */
export function isNewAccount(createdAt: string | null | undefined, now: number = Date.now()): boolean {
  if (!createdAt) return false;
  const created = Date.parse(createdAt);
  return !Number.isNaN(created) && now - created >= -60_000 && now - created <= NEW_ACCOUNT_MS;
}

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: Fbq;
  loaded: boolean;
  version: string;
  disablePushState?: boolean;
};

let fbq: Fbq | null = null;
let revoked = false;
/** Steps sent this page load — Lead/PageView/ViewContent once each. */
const sentThisLoad = new Set<string>();
/** A page view skipped because a UK/EU visitor hadn't chosen yet; sent on Accept. */
let pageViewAwaitingConsent = false;
/** The same for a new account's CompleteRegistration (the account id). */
let registrationAwaitingConsent: { userId: string; createdAt: string } | null = null;
/** Accounts already sent this page load (useAuth runs once per mounted hook,
 *  and localStorage can be unavailable). */
const registeredThisLoad = new Set<string>();

function isNative(): boolean {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
}

function allowedNow(): boolean {
  if (typeof window === "undefined") return false;
  return pixelAllowed({
    host: window.location.hostname, native: isNative(), mode: currentAnalyticsMode(), embedded: isEmbedded(),
  });
}

/** Meta's standard loader, without the inline <script>: a queueing stub until
 *  fbevents.js arrives and takes over. */
function loadPixel(): Fbq {
  if (fbq) return fbq;
  const w = window as unknown as { fbq?: Fbq; _fbq?: Fbq };
  const stub = function (...args: unknown[]) {
    if (stub.callMethod) stub.callMethod(...args);
    else stub.queue.push(args);
  } as Fbq;
  stub.push = stub;
  stub.loaded = true;
  stub.version = "2.0";
  stub.queue = [];
  stub.disablePushState = true; // no automatic page view per in-app navigation
  w.fbq = stub;
  w._fbq = stub;
  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);
  stub("set", "autoConfig", false, META_PIXEL_ID);
  stub("init", META_PIXEL_ID);
  fbq = stub;
  return stub;
}

const FBC_COOKIE = "_fbc";
const FBC_MAX_AGE_S = 90 * 24 * 60 * 60;

/** Pure: Meta's click cookie, in the form fbevents.js writes it
 *  (fb.<subdomain index>.<creation ms>.<fbclid>); null for a bad click id. */
export function fbcCookieValue(fbclid: string | null | undefined, now: number = Date.now()): string | null {
  const id = validFbclid(fbclid);
  return id ? `fb.1.${now}.${id}` : null;
}

function hasCookie(name: string): boolean {
  try {
    return document.cookie.split("; ").some((c) => c.startsWith(`${name}=`));
  } catch {
    return false;
  }
}

/**
 * Credit a conversion to the ad after a browser switch. fbevents.js creates
 * `_fbc` from the `fbclid` in the address when it loads — which is the ad's
 * own landing. A sign-up confirmed in Safari (or carried over by the in-app
 * hand-off link) arrives with the click id in this browser's storage instead
 * (utmCapture: withCarriedAttribution → captureUtmFromUrl), and the address
 * tidied long before the Pixel loads here. So write the cookie once ourselves,
 * before loading — nothing Meta wouldn't have set on the landing page. Without
 * it CompleteRegistration is counted but attributed to no ad, which is what
 * the ad report then shows. Only ever called on a send, i.e. with consent.
 */
function ensureFbcCookie(): void {
  try {
    if (hasCookie(FBC_COOKIE)) return;
    const value = fbcCookieValue(getStoredUtm()?.fbclid);
    if (!value) return;
    const domain = window.location.hostname.replace(/^www\./, "");
    document.cookie = `${FBC_COOKIE}=${value}; Max-Age=${FBC_MAX_AGE_S}; path=/; domain=.${domain}; SameSite=Lax`;
  } catch { /* no document, or cookies blocked */ }
}

function send(name: string, eventId?: string): void {
  ensureFbcCookie();
  const pixel = loadPixel();
  if (revoked) {
    pixel("consent", "grant");
    revoked = false;
  }
  // fbq("track", name, customData, { eventID }) — Meta drops repeats of an
  // event with the same eventID, which is what keeps one account's sign-up
  // from counting once per device it signs in on.
  if (eventId) pixel("track", name, {}, { eventID: eventId });
  else pixel("track", name);
}

/** Called for every PostHog event (lib/posthog.ts capturePostHog). */
export function trackMetaFor(event: string, props: Record<string, unknown> = {}): void {
  try {
    const name = metaEventFor(event, props);
    if (!name || typeof window === "undefined") return;
    if (!allowedNow()) {
      if (name === "PageView" && currentAnalyticsMode() === "pending") pageViewAwaitingConsent = true;
      return;
    }
    if (hasUrlSecrets(window.location.href)) return;
    if (sentThisLoad.has(name)) return;
    sentThisLoad.add(name);
    send(name);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("meta pixel failed:", err);
  }
}

/** A stable, non-reversible tag for an account id (53-bit cyrb53 hash) — the
 *  dedupe key Meta sees, so our own account id never leaves for Meta. */
export function opaqueAccountTag(userId: string): string {
  let h1 = 0xdeadbeef ^ 0x7a1b, h2 = 0x41c6ce57 ^ 0x7a1b;
  for (let i = 0; i < userId.length; i++) {
    const ch = userId.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

function alreadyRegistered(userId: string): boolean {
  if (registeredThisLoad.has(userId)) return true;
  try { return localStorage.getItem(REGISTERED_KEY) === userId; } catch { return false; }
}

/**
 * CompleteRegistration — called by useAuth once the signed-in account has
 * loaded. Sent once per account per device, only for an account created in
 * the last 48h. Right after the email link or Google the address still holds
 * the sign-in keys for a moment, so it waits (up to 10 s) for the auth
 * client to tidy it; a UK/EU visitor who hasn't chosen gets it on Accept.
 */
export function trackMetaRegistration(userId: string | null | undefined, createdAt: string | null | undefined, attempt = 0): void {
  try {
    if (!userId || !createdAt || typeof window === "undefined") return;
    if (!isNewAccount(createdAt) || alreadyRegistered(userId)) return;
    if (!allowedNow()) {
      if (currentAnalyticsMode() === "pending") registrationAwaitingConsent = { userId, createdAt };
      return;
    }
    if (hasUrlSecrets(window.location.href)) {
      if (attempt < 20) window.setTimeout(() => trackMetaRegistration(userId, createdAt, attempt + 1), 500);
      return;
    }
    registeredThisLoad.add(userId);
    try { localStorage.setItem(REGISTERED_KEY, userId); } catch { /* private mode — the Set covers this load */ }
    // The "once per device" guard above can't see other devices; a stable
    // per-account eventID lets Meta dedupe a sign-up seen on phone + laptop.
    const eventId = `reg_${opaqueAccountTag(userId)}`;
    send("CompleteRegistration", eventId);
    reportRegistrationToServer(eventId);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("meta pixel failed:", err);
  }
}

function cookieValue(name: string): string | null {
  try {
    const hit = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
    return hit ? decodeURIComponent(hit.slice(name.length + 1)) : null;
  } catch {
    return null;
  }
}

/** How long to let fbevents.js arrive and set `_fbp` before reading it. */
export const SERVER_REPORT_DELAY_MS = 3000;

/**
 * The same CompleteRegistration, sent by our server too (Conversions API,
 * backend/services/meta_capi.py, 2026-10-27). A browser that blocks Meta's
 * script, or trims its cookies, loses the Pixel's copy and the ad report
 * under-counts sign-ups. Same event id, so Meta keeps one of the two. Only
 * ever called right after a Pixel send — i.e. on trybeup.com, never in the
 * app, and only with consent — and it carries only Meta's own two cookies;
 * the server adds the IP and browser details. Best-effort: a failure is
 * silent.
 */
function reportRegistrationToServer(eventId: string): void {
  window.setTimeout(() => {
    void (async () => {
      try {
        const { apiFetch } = await import("@/lib/api");
        await apiFetch("/api/analytics/meta-registration", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ eventId, fbc: cookieValue(FBC_COOKIE), fbp: cookieValue("_fbp") }),
        });
      } catch { /* offline, or signed out meanwhile */ }
    })();
  }, SERVER_REPORT_DELAY_MS);
}

/** Consent banner → Accept: send what was held back for the choice. */
export function grantMetaPixel(): void {
  if (fbq && revoked) { fbq("consent", "grant"); revoked = false; }
  if (pageViewAwaitingConsent) {
    pageViewAwaitingConsent = false;
    trackMetaFor("gateway.viewed");
  }
  if (registrationAwaitingConsent) {
    const { userId, createdAt } = registrationAwaitingConsent;
    registrationAwaitingConsent = null;
    trackMetaRegistration(userId, createdAt);
  }
}

/** Consent banner → Decline (or a later change of mind): stop sending and
 *  remove Meta's browser id cookie. */
export function denyMetaPixel(): void {
  pageViewAwaitingConsent = false;
  registrationAwaitingConsent = null;
  try {
    if (fbq && !revoked) { fbq("consent", "revoke"); revoked = true; }
    const domain = window.location.hostname.replace(/^www\./, "");
    for (const d of [`; domain=.${domain}`, ""]) {
      document.cookie = `_fbp=; Max-Age=0; path=/${d}`;
      document.cookie = `_fbc=; Max-Age=0; path=/${d}`;
    }
  } catch { /* no document */ }
}

/** Test-only: forget this module's state. */
export function _resetMetaPixelForTests(): void {
  fbq = null;
  revoked = false;
  sentThisLoad.clear();
  registeredThisLoad.clear();
  pageViewAwaitingConsent = false;
  registrationAwaitingConsent = null;
}
